/**
 * CategoryBreakdown: Microsoft Secure Score by category (Identity, Data,
 * Apps, Device) as Carbon framework-style tiles in a condensed grid (mockup
 * 03 "framework tiles"): the category, a 2rem light percentage, a helper line
 * with the points and open recommendations, and an 8px bar in the Secure
 * Score mark (cat-secure, Microsoft's score, not a Guardian pillar). The
 * score's level is carried by the number, never by a hue (DESIGN.md "The
 * Neutral Bad State Rule").
 */
import { BigNumber } from '@/components/carbon/BigNumber'
import { CondensedGrid } from '@/components/carbon/CondensedGrid'
import { ScoreBar } from '@/components/carbon/ScoreBar'
import { Tile } from '@/components/carbon/Tile'
import { TileHeader } from '@/components/carbon/TileHeader'
import type { SecureScoreByCategory, CategoryScore } from '@/types'

interface CategoryBreakdownProps {
  breakdown: SecureScoreByCategory
}

const CATEGORY_ORDER: ReadonlyArray<{ key: keyof SecureScoreByCategory; label: string }> = [
  { key: 'identity', label: 'Identity' },
  { key: 'data', label: 'Data' },
  { key: 'apps', label: 'Apps' },
  { key: 'device', label: 'Device' },
]

export function CategoryBreakdown({ breakdown }: CategoryBreakdownProps) {
  return (
    <CondensedGrid as="ul" columns={{ base: 1, 'c-md': 2, 'c-xlg': 4 }} aria-label="Secure Score by category">
      {CATEGORY_ORDER.map(({ key, label }) => (
        <CategoryCard key={key} label={label} score={breakdown?.[key]} />
      ))}
    </CondensedGrid>
  )
}

interface CategoryCardProps {
  label: string
  score: CategoryScore | undefined
}

function CategoryCard({ label, score }: CategoryCardProps) {
  // Microsoft can omit a category; that is "no data", never a fabricated 0%.
  const known = score != null && Number.isFinite(score.percentage)
  const pct = known ? Math.round(score.percentage) : null
  const name = score?.category || label

  return (
    <Tile as="li" className="flex min-w-0 flex-col pb-5">
      <TileHeader title={name} level={3} />
      <BigNumber value={pct} suffix="%" size="figure" className="mt-3" />
      <p className="m-0 mt-0.5 text-helper text-text-helper">
        {known ? (
          <>
            <span className="tabular-nums">
              {Math.round(score.currentScore)} of {Math.round(score.maxScore)}
            </span>{' '}
            points
            {score.recommendationCount > 0
              ? ` · ${score.recommendationCount} open recommendation${score.recommendationCount === 1 ? '' : 's'}`
              : ''}
          </>
        ) : (
          'Microsoft has not scored this category yet.'
        )}
      </p>
      {known ? <ScoreBar value={score.percentage} category="secure" label={`${name} Secure Score`} className="mt-4" /> : null}
    </Tile>
  )
}
